import { IPosition } from '../../types/common.types';
import {
  IRoom,
  MapStyle,
  RoomShape,
  RoomShapeFamily,
} from '../../types/map.types';
import { STYLE_PRESETS } from './style-presets';
import { maskCells } from './mask';
import { sampleRoomShape } from './room-shapes';

export interface PlacedRoom {
  shape: RoomShape;
  x: number;
  y: number;
  center: IPosition;
  cells: IPosition[];
  family: RoomShapeFamily;
  templateId?: string;
}

function rectFreeWithPadding(
  occupied: boolean[][],
  x: number,
  y: number,
  w: number,
  h: number,
  pad: number,
  width: number,
  height: number,
): boolean {
  for (let yy = y - pad; yy < y + h + pad; yy++) {
    for (let xx = x - pad; xx < x + w + pad; xx++) {
      if (xx < 1 || yy < 1 || xx >= width - 1 || yy >= height - 1) {
        return false;
      }
      if (occupied[yy][xx]) {
        return false;
      }
    }
  }
  return true;
}

function commit(occupied: boolean[][], placed: PlacedRoom): void {
  for (const c of placed.cells) {
    occupied[c.y][c.x] = true;
  }
}

export function toRoom(placed: PlacedRoom): IRoom {
  const { x, y, shape, cells } = placed;
  return {
    x,
    y,
    width: shape.width,
    height: shape.height,
    left: x,
    right: x + shape.width - 1,
    top: y,
    bottom: y + shape.height - 1,
    cells,
  };
}

function place(
  shape: RoomShape,
  occupied: boolean[][],
  pad: number,
  width: number,
  height: number,
  family: RoomShapeFamily,
  templateId?: string,
): PlacedRoom | null {
  const maxAttempts = 60;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const x = 1 + Math.floor(Math.random() * (width - shape.width - 2));
    const y = 1 + Math.floor(Math.random() * (height - shape.height - 2));
    if (
      !rectFreeWithPadding(
        occupied,
        x,
        y,
        shape.width,
        shape.height,
        pad,
        width,
        height,
      )
    ) {
      continue;
    }
    const cells = maskCells(shape.mask).map((c) => ({
      x: x + c.x,
      y: y + c.y,
    }));
    const center = cells[Math.floor(cells.length / 2)] ?? {
      x: x + Math.floor(shape.width / 2),
      y: y + Math.floor(shape.height / 2),
    };
    const placed: PlacedRoom = {
      shape,
      x,
      y,
      center,
      cells,
      family,
      templateId,
    };
    commit(occupied, placed);
    return placed;
  }
  return null;
}

/**
 * Places the prefab/template shapes first (they are scarce and large), then
 * fills up to `targetCount` with procedural shapes. Returns the rooms in
 * placement order; templates keep their `templateId`.
 */
export function placeRooms(
  width: number,
  height: number,
  style: MapStyle,
  targetCount: number,
  templateShapes: { id: string; shape: RoomShape; padding: number }[],
): PlacedRoom[] {
  const occupied: boolean[][] = [];
  for (let y = 0; y < height; y++) {
    occupied[y] = new Array<boolean>(width).fill(false);
  }
  const p = STYLE_PRESETS[style];
  const placed: PlacedRoom[] = [];

  for (const t of templateShapes) {
    const room = place(
      t.shape,
      occupied,
      t.padding,
      width,
      height,
      'prefab',
      t.id,
    );
    if (room) {
      placed.push(room);
    }
  }

  let guard = 0;
  while (placed.length < targetCount && guard < targetCount * 12) {
    guard++;
    const { shape, family } = sampleRoomShape(style);
    const room = place(shape, occupied, p.roomPadding, width, height, family);
    if (room) {
      placed.push(room);
    }
  }
  return placed;
}
